import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCoreRowModel, getExpandedRowModel, useReactTable } from "@tanstack/react-table";
import { usePersistedTableState } from "components/table/usePersistedTableState";
import { useAppSelector } from "redux/hooks";
import { apiClient } from "zodios/client";
import { HStack, VStack } from "../../../styled-system/jsx";
import { Button } from "components/core/basic/Button";
import { resetTable } from "components/table/utils";
import { MdRotateLeft } from "react-icons/md";
import { RT8ColumnSelector } from "components/core/blocks/MetricSelector";
import { SearchInput } from "components/fields/base/TextField";
import { css } from "../../../styled-system/css";
import { RT8FilterWidget } from "views/merchandise/tables/FilterWidget";
import { RT8Pagination } from "components/table/Pagination";
import { Table } from "components/table/Table";
import ExpanderContainer from "components/core/basic/ExpanderContainer";
import { useForm, useWatch } from "react-hook-form";
import { HiddenField, InputField } from "./programs/fields";
import { Form } from "react-bootstrap";
import { getCurrencySymbol } from "utils/formatNumber";
import { successToast } from "utils/toasts";

const PortfolioForm = ({ portfolioId, scheduledAction = null }) => {
  const queryClient = useQueryClient();
  const form = useForm({
    defaultValues: {
      enableBudgetSchedule: scheduledAction?.ui_state?.enableBudgetSchedule ?? false,
      schedule: scheduledAction?.ui_state?.schedule ?? {
        type: "daily",
        amount: 0,
      },
    },
  });
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const currencySymbol = getCurrencySymbol(marketPlace);
  const enableBudgetSchedule = useWatch({
    control: form.control,
    name: "enableBudgetSchedule",
  });

  const updateScheduledAction = useMutation({
    mutationFn: async ({ id, ui_state }) => {
      const cron = ui_state.schedule?.type === "daily" ? "0 0 * * *" : null;
      const body = {
        id,
        ui_state,
        is_enabled: ui_state.enableBudgetSchedule,
        cron,
        action_type: "update_portfolio_budget",
        start_date: scheduledAction?.start_date || new Date().toISOString(),
        end_date: scheduledAction?.end_date || null,
      };
      return await apiClient.updateScheduleSyncAction(body, {
        params: { id },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["scheduledSyncAction", "portfolio"],
      });
      successToast({ message: "Portfolio Budget Schedule updated successfully." });
    },
  });

  const createScheduledAction = useMutation({
    mutationFn: async (ui_state) => {
      const cron = ui_state.schedule?.type === "daily" ? "0 0 * * *" : null;
      const body = {
        ui_state,
        content_type: "gvportfolio",
        entity_id: portfolioId,
        is_enabled: ui_state.enableBudgetSchedule,
        cron,
        action_type: "update_portfolio_budget",
        start_date: scheduledAction?.start_date || new Date().toISOString(),
        end_date: scheduledAction?.end_date || null,
      };
      return await apiClient.createScheduleSyncAction(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["scheduledSyncAction", "portfolio"],
      });
      successToast({ message: "Portfolio Budget Schedule updated successfully." });
    },
  });

  const onSubmit = (data) => {
    const ui_state = data;
    if (scheduledAction) {
      updateScheduledAction.mutate({ id: scheduledAction.id, ui_state });
    } else {
      createScheduledAction.mutate(ui_state);
    }
  };

  return (
    <form
      className={css({ maxWidth: "400px", padding: "16px" })}
      onSubmit={form.handleSubmit((data) => {
        onSubmit(data);
      })}
    >
      <Form.Check type="checkbox" label="Enable Daily Budget Schedule" {...form.register("enableBudgetSchedule")} />
      <HiddenField
        control={form.control}
        fieldName={"schedule.type"}
        defaultValue={"daily"}
        formState={form.formState}
      />
      <InputField
        disabled={!enableBudgetSchedule}
        label="Budget Amount"
        type="number"
        control={form.control}
        fieldName={"schedule.amount"}
        formState={form.formState}
        symbol={currencySymbol}
      />
      <Button
        isDisabled={createScheduledAction?.isPending || updateScheduledAction.isPending}
        type="submit"
        variant="primary"
        className={css({ marginTop: "16px" })}
      >
        Save
      </Button>
    </form>
  );
};

const PortfolioFormWrapper = ({ portfolioId }) => {
  const { data: scheduledSyncAction, isLoading } = useQuery({
    queryKey: ["scheduledSyncAction", "portfolio", portfolioId],
    queryFn: () =>
      apiClient.getScheduleSyncActions({ queries: { entity_id: portfolioId, content_type__model: "gvportfolio" } }),
    options: {
      refetchOnMount: true,
      enabled: !!portfolioId,
    },
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const scheduledAction = scheduledSyncAction?.results?.[0];

  return <PortfolioForm scheduledAction={scheduledAction} portfolioId={portfolioId} />;
};

const PortfoliosTable = () => {
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const defaultSelectedColumns = ["portfolio_name"];
  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "portfolios",
    initialState: {
      columnOrder: defaultSelectedColumns,
      columnPinning: {
        left: ["portfolio_name"],
      },
      sorting: [{ id: "portfolio_name" }],
    },
  });
  const portfolios = useQuery({
    queryKey: [
      "portfolios",
      marketPlace,
      state.pagination.pageIndex,
      state.pagination.pageSize,
      state.sorting,
      state.globalFilter,
    ],
    queryFn: () => {
      const ordering = state.sorting?.[0] || { id: "name", desc: false };
      const queries = {
        page: state.pagination.pageIndex + 1,
        limit: state.pagination.pageSize,
        ordering: `${ordering.desc ? "-" : ""}${ordering.id}`,
        market_place: marketPlace,
      };
      return apiClient.getPortfolios({
        queries,
      });
    },
    options: {
      refetchOnMount: true,
    },
  });

  const columns = [
    { accessorKey: "portfolio_name", header: "Name" },
    { accessorKey: "portfolio_state", header: "State" },
    { accessorKey: "portfolio_budget_amount", header: "Budget Amount" },
    { accessorKey: "portfolio_budget_type", header: "Budget Type" },
    { accessorKey: "portfolio_budget_start_date", header: "Start Date" },
    { accessorKey: "portfolio_budget_end_date", header: "End Date" },
  ];

  const table = useReactTable({
    data: portfolios.data?.results ?? [],
    columns,
    defaultColumn: {
      filterFn: () => true,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    getRowId: (row) => row.portfolio_id?.toString?.() ?? row.id?.toString?.(),
    rowCount: portfolios.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    manualPagination: true,
    enableRowSelection: false,
    meta: {
      showSkeleton: portfolios.isFetching && (!portfolios.data || portfolios.isPlaceholderData),
      // columnFilterSelectOptions: ... // define if needed
    },
  });
  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={"Search"}
              isExpandable
              className={css({ flex: "0 1 350px" })}
              value={table.getState().globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => {
          return (
            <ExpanderContainer>
              <PortfolioFormWrapper portfolioId={row.id} />
            </ExpanderContainer>
          );
        }}
      />
    </>
  );
};

const AmazonPortfolios = () => {
  return (
    <div>
      <PortfoliosTable />
    </div>
  );
};

export default AmazonPortfolios;
